import { SlidersHorizontal, ToggleLeft, ToggleRight } from 'lucide-react';
import { toast } from 'sonner';
import { useBreadcrumbs } from '@/client/hooks/use-breadcrumbs';
import { useListNotificationTypesQuery } from '@/client/api/notifications/list-notification-types.query';
import { useUpdateNotificationTypeMutation } from '@/client/api/notifications/update-notification-type.mutation';
import { NotificationType } from '@/client/api/notifications/list-notification-types.query';

type Channel = 'in_app' | 'email' | 'sms';

const NotificationsPreferencesPage = () => {
  useBreadcrumbs([
    { label: 'Communications', href: '/admin/communications/notifications' },
    { label: 'Notifications' },
    { label: 'Preferences' },
  ]);

  const { data, isLoading, refetch } = useListNotificationTypesQuery();
  const types = data?.data?.notification_types || [];
  const updateMutation = useUpdateNotificationTypeMutation();

  const toggleDefault = (type: NotificationType, channel: Channel) => {
    const field = `default_${channel}` as const;
    updateMutation.mutate(
      { ntypid: type.ntypid, [field]: !type[field] },
      {
        onSuccess: () => refetch(),
        onError: (error) => toast.error(error.message || 'Failed to update notification type'),
      }
    );
  };

  const renderToggle = (type: NotificationType, channel: Channel, allowed: boolean, value: boolean) => (
    <button
      onClick={() => toggleDefault(type, channel)}
      disabled={updateMutation.isPending || !allowed}
      title={!allowed ? `${channel} isn't available for this type` : value ? 'Turn off by default' : 'Turn on by default'}
      className="disabled:opacity-30"
    >
      {value
        ? <ToggleRight className="w-6 h-6 text-green-500 ml-auto" />
        : <ToggleLeft className="w-6 h-6 text-gray-400 ml-auto" />}
    </button>
  );

  return (
    <div className="min-h-screen bg-gray-50 p-6">
      <div className="mb-6">
        <div className="flex items-center gap-3 mb-1">
          <SlidersHorizontal className="h-7 w-7 text-blue-700" />
          <h1 className="text-2xl font-bold text-gray-900">Notification preferences</h1>
        </div>
        <p className="ml-10 max-w-2xl text-sm text-gray-500">
          Default channel behavior for new users, per notification type. Users can still override these individually.
        </p>
      </div>

      <div className="mb-6 grid gap-4 sm:grid-cols-3">
        <div className="rounded-xl border border-gray-100 bg-white p-4 shadow-sm">
          <p className="text-sm text-gray-500">Email default-on</p>
          <p className="mt-1 text-2xl font-semibold text-gray-900">{types.filter(t => t.default_email).length}</p>
        </div>
        <div className="rounded-xl border border-gray-100 bg-white p-4 shadow-sm">
          <p className="text-sm text-gray-500">SMS default-on</p>
          <p className="mt-1 text-2xl font-semibold text-gray-900">{types.filter(t => t.default_sms).length}</p>
        </div>
        <div className="rounded-xl border border-gray-100 bg-white p-4 shadow-sm">
          <p className="text-sm text-gray-500">In-app default-on</p>
          <p className="mt-1 text-2xl font-semibold text-gray-900">{types.filter(t => t.default_in_app).length}</p>
        </div>
      </div>

      <div className="overflow-hidden rounded-xl border border-gray-100 bg-white shadow-sm">
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">Type</th>
                <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">Category</th>
                <th className="px-6 py-3 text-right text-xs font-medium uppercase tracking-wider text-gray-500">Email</th>
                <th className="px-6 py-3 text-right text-xs font-medium uppercase tracking-wider text-gray-500">SMS</th>
                <th className="px-6 py-3 text-right text-xs font-medium uppercase tracking-wider text-gray-500">In-app</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100 bg-white">
              {isLoading && (
                <tr><td colSpan={5} className="px-6 py-16 text-center text-sm text-gray-500">Loading...</td></tr>
              )}
              {!isLoading && types.length === 0 && (
                <tr><td colSpan={5} className="px-6 py-16 text-center text-sm text-gray-500">No notification types found</td></tr>
              )}
              {types.map((type) => (
                <tr key={type.ntypid} className="hover:bg-gray-50">
                  <td className="px-6 py-4">
                    <div className="text-sm font-medium text-gray-900">{type.name}</div>
                    <div className="mt-0.5 text-xs text-gray-500 font-mono">{type.name_system}</div>
                  </td>
                  <td className="px-6 py-4 text-sm text-gray-600 capitalize">{type.category || '—'}</td>
                  <td className="px-6 py-4 text-right">{renderToggle(type, 'email', type.allow_email, type.default_email)}</td>
                  <td className="px-6 py-4 text-right">{renderToggle(type, 'sms', type.allow_sms, type.default_sms)}</td>
                  <td className="px-6 py-4 text-right">{renderToggle(type, 'in_app', type.allow_in_app, type.default_in_app)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default NotificationsPreferencesPage;
